import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  useTheme,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowBackIosNewOutlined from "@mui/icons-material/ArrowBackIosNewOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useNavigate } from "react-router-dom";
import {
  ApplicationType,
  entity_id,
  locationGeo,
  orderID,
  Platform,
} from "../../Utils/Const";
import { baseapi } from "../../Environmentalurl";
import DashboardHeader from "../../Dashboard/DashboardHeader";
import api from "../../Utils/Api";

const STATIC_MACHINE_ID = "0409634b9bc2de2c";

const CONSENT_POINTS = [
  "I give my consent for any credit or debit in my wallet required to close this account, which will be initiated by Jan Aadhaar eWallet.",
  "I understand that no further transactions will be allowed in the wallet after it is closed. The request has been made.",
  "Please note that we will initiate an IMPS transaction of ₹1 to verify your bank account, and the charge will be deducted from your wallet.",
  "If the name on your bank account does not match the name of the wallet holder, we will not allow closure until the correct bank account is provided. This is necessary to return the balance in your account. If your balance is zero, we will close the account without bank account verification.",
  "Please be patient as it will take at least 7 days to process this request.",
];

export default function WalletClosure() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [bankName, setBankName] = useState("");
  const [acc, setAcc] = useState("");
  const [confirmAcc, setConfirmAcc] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [accountHolder, setAccountHolder] = useState("");

  const [email] = useState(JSON.parse(sessionStorage.getItem("Poiemail") || "null"));
  const [reason, setReason] = useState("Unable to upgrade wallet");

  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [consentOpen, setConsentOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ---------------- FETCH ACCOUNT DETAILS ----------------
  useEffect(() => {
    const fetchAccountDetails = async () => {
      setFetching(true);
      try {
        const mobileNumber = JSON.parse(sessionStorage.getItem("mobileNumber") || "null");
        const SessionID = JSON.parse(sessionStorage.getItem("SessionID") || "null");

        const payload = {
          ReqType: "TAGetAccountDetails",
          MachineID: STATIC_MACHINE_ID,
          MobileNumber: mobileNumber,
          SessionID: SessionID,
          Statement: "GetAccountDetails",
          Platform: Platform,
          EntityID: entity_id,
          GeoLocation: locationGeo,
        };

        const response = await api.post(`${baseapi}/accountdetails`, payload);
        const { Response_Code, Message, Info } = response.data;

        if (String(Response_Code) === "1000") {
          const row = Info?.Table?.[0] || {};
          setBankName(row.PDBankName || "");
          setAcc(row.PDBankAccountNo || "");
          setConfirmAcc(row.PDBankAccountNo || "");
          setIfsc(row.PDBankIFSC || "");
          setAccountHolder(row.PDBankAccountHolderName || "");
        } else {
          showSnackbar(Message || "Failed to load account details", "error");
        }
      } catch (error) {
        console.error("Account details error:", error);
        showSnackbar("Server error while loading account details.", "error");
      } finally {
        setFetching(false);
      }
    };

    fetchAccountDetails();
  }, []);

  // ---------------- PROCEED → open consent ----------------
  const handleProceed = () => {
    if (!acc || !ifsc || !bankName) {
      showSnackbar("Account details are not available.", "error");
      return;
    }
    setConsentOpen(true);
  };

  // ---------------- AGREE → call closure API ----------------
  const handleAgree = async () => {
    setConsentOpen(false);
    setSubmitting(true);

    const NameOnCard = JSON.parse(sessionStorage.getItem("NameOnCard") || "null");
    const mobileNumber = JSON.parse(sessionStorage.getItem("mobileNumber") || "null");
    const Poiemail = JSON.parse(sessionStorage.getItem("Poiemail") || "null");

    const payload = {
      ReqType: "Jan_Aadhaar_eWallet_AccountClosure_Insert",
      Statement: "Insert",
      MobileNumber: mobileNumber,
      AccountHolderStatus: "Active",
      KYCStatus: "FullKYC",
      WalletBalance: "0.00",
      EmailID: Poiemail,
      BankName: bankName,
      BankAccountNumber: acc,
      BankIFSC: ifsc,
      OrderID: orderID,
      CustomerConsent:
        "I am giving my consent for any credit or debit to my wallet needed for the closure of this account which will be initiated by TA.",
      Reasonforclosing: reason,
      Platform: Platform,
      ApplicationType: ApplicationType,
      GeoLocation: locationGeo,
      EntityID: entity_id,
      PayerName: NameOnCard,
    };

    try {
      const response = await api.post(
        `${baseapi}/JanAadhaareWalletAccountClosureInsert`,
        payload,
      );

      const { Response_Code, Message } = response.data;

      showSnackbar(
        Message || "Something went wrong",
        String(Response_Code) === "1001" ? "success" : "info",
      );

      if (String(Response_Code) === "1001") {
        setTimeout(() => {
          navigate("/profile-settings/walletclouserreq/otp");
        }, 1500);
      }
    } catch (error) {
      console.error("API Error:", error);
      showSnackbar("Server error. Try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate("/profile-settings");

  const inputBoxSx = {
    bgcolor: isDark ? "#0f172a" : "#f1f5f9",
    height: 52,
    borderRadius: 2,
  };
  const disabledTextSx = {
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: isDark ? "#cbd5e1" : "#334155",
    },
  };

  return (
    <>
      <DashboardHeader />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          bgcolor: isDark ? "#0d1523" : "#f5f7fb",
        }}
      >
        <Box mt={2} bgcolor={isDark ? "#0d1523" : "#f5f7fb"}>
          <Box sx={{ width: "100%", maxWidth: 600, mx: "auto" }}>
            <Card
              sx={{
                borderRadius: 4,
                p: { xs: 2, md: 4 },
                boxShadow: "0px 8px 40px rgba(0,0,0,0.08)",
                bgcolor: isDark ? "#0b1220" : "#ffffff",
              }}
            >
              {/* ======== Title + Step ======== */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 3,
                  gap: 1.5,
                }}
              >
                <IconButton
                  onClick={handleBack}
                  sx={{
                    bgcolor: "white",
                    color: "#1e5fd8",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                  }}
                >
                  <ArrowBackIosNewOutlined />
                </IconButton>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: isDark ? "#e2e8f0" : "#0f172a" }}
                >
                  TA Wallet Closure Request
                </Typography>

                <Box
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: 12,
                    bgcolor: isDark ? "#1e293b" : "#eef2ff",
                    color: isDark ? "#cbd5e1" : "#3b82f6",
                  }}
                >
                  Step 1 of 2
                </Box>
              </Box>

              {fetching ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    gap: 2,
                  }}
                >
                  <CircularProgress sx={{ color: "#16a34a" }} />
                  <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                    Loading your account details...
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* BANK NAME */}
                  <Typography sx={{ color: isDark ? "#94a3b8" : "#475569", mb: 1 }}>
                    Bank Name
                  </Typography>
                  <TextField
                    fullWidth
                    value={bankName}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountBalanceIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                      sx: inputBoxSx,
                    }}
                    sx={{ mb: 3, ...disabledTextSx }}
                  />

                  {/* ACCOUNT NUMBER */}
                  <Typography sx={{ mb: 1 }}>Account Number</Typography>
                  <TextField
                    fullWidth
                    value={acc}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                      sx: inputBoxSx,
                    }}
                    sx={{ mb: 3, ...disabledTextSx }}
                  />

                  {/* CONFIRM ACCOUNT NUMBER */}
                  <Typography sx={{ mb: 1 }}>Confirm Account Number</Typography>
                  <TextField
                    fullWidth
                    value={confirmAcc}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                      sx: inputBoxSx,
                    }}
                    sx={{ mb: 3, ...disabledTextSx }}
                  />

                  {/* IFSC */}
                  <Typography sx={{ mb: 1 }}>IFSC Code</Typography>
                  <TextField
                    fullWidth
                    value={ifsc}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                      sx: inputBoxSx,
                    }}
                    sx={{ mb: 3, ...disabledTextSx }}
                  />

                  {/* ACCOUNT HOLDER */}
                  {accountHolder && (
                    <>
                      <Typography sx={{ mb: 1 }}>Account Holder Name</Typography>
                      <TextField
                        fullWidth
                        value={accountHolder}
                        disabled
                        InputProps={{ sx: inputBoxSx }}
                        sx={{ mb: 3, ...disabledTextSx }}
                      />
                    </>
                  )}

                  {/* EMAIL */}
                  <Typography sx={{ mb: 1 }}>Email</Typography>
                  <TextField
                    fullWidth
                    value={email}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                      sx: inputBoxSx,
                    }}
                    sx={{ mb: 3, ...disabledTextSx }}
                  />

                  {/* REASON */}
                  <Typography sx={{ mb: 1 }}>Reason for Wallet Closure</Typography>
                  <Select
                    fullWidth
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{
                      bgcolor: isDark ? "#0f172a" : "#f8fafc",
                      height: 52,
                      borderRadius: 2,
                      mb: 3,
                    }}
                  >
                    <MenuItem value="Unable to upgrade wallet">
                      Unable to upgrade wallet
                    </MenuItem>
                    <MenuItem value="Not using wallet">Not using wallet</MenuItem>
                    <MenuItem value="Security concerns">Security concerns</MenuItem>
                  </Select>

                  {/* ALERT */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: isDark ? "#0f172a" : "#eef6ff",
                      borderRadius: 2,
                      color: isDark ? "#cbd5e1" : "#3b82f6",
                      fontSize: 14,
                      mb: 3,
                    }}
                  >
                    You will receive an OTP on your registered mobile and email to
                    verify this request.
                  </Box>

                  {/* PROCEED */}
                  <Button
                    fullWidth
                    onClick={handleProceed}
                    disabled={submitting}
                    sx={{
                      bgcolor: "#16a34a",
                      color: "white",
                      py: 1.4,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: 16,
                      "&:hover": { bgcolor: "#15803d" },
                      "&.Mui-disabled": { bgcolor: isDark ? "#1e293b" : "#e2e8f0" },
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={22} sx={{ color: "white" }} />
                    ) : (
                      "PROCEED"
                    )}
                  </Button>

                  <Typography
                    sx={{
                      mt: 2,
                      fontSize: 12,
                      color: "#64748b",
                      textAlign: "center",
                    }}
                  >
                    Note: Wallet closure will permanently disable all wallet
                    services. Your remaining balance will be transferred to the
                    provided bank account.
                  </Typography>
                </>
              )}
            </Card>
          </Box>
        </Box>


        <Dialog
          open={consentOpen}
          onClose={() => setConsentOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,            // ← square corners
              width: "100%",
              maxWidth: 440,
              overflow: "hidden",
              bgcolor: isDark ? "#0b1220" : "#ffffff",
              border: isDark ? "1px solid #1e293b" : "1px solid #e2e8f0",
            },
          }}
          BackdropProps={{
            sx: { backdropFilter: "blur(3px)", backgroundColor: "rgba(0,0,0,0.55)" },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "#16a34a",
            }}
          >
            <VerifiedUserOutlinedIcon sx={{ color: "#fff" }} />
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>
              Consent
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ px: 3, py: 2.5, maxHeight: "55vh", overflowY: "auto" }}>
            {CONSENT_POINTS.map((point, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  mb: 1.5,
                  pb: 1.5,
                  borderBottom:
                    i !== CONSENT_POINTS.length - 1
                      ? isDark
                        ? "1px solid #1e293b"
                        : "1px solid #f1f5f9"
                      : "none",
                }}
              >
                <Box
                  sx={{
                    minWidth: 26,
                    height: 26,
                    borderRadius: 0, // ← square number badge
                    bgcolor: isDark ? "#14532d" : "#dcfce7",
                    color: "#16a34a",
                    fontSize: 13,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.2,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.65,
                    color: isDark ? "#cbd5e1" : "#334155",
                  }}
                >
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Actions */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              gap: 1.5,
              borderTop: isDark ? "1px solid #1e293b" : "1px solid #e2e8f0",
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setConsentOpen(false)}
              sx={{
                borderRadius: 0,          // ← square button
                py: 1.2,
                textTransform: "none",
                fontWeight: 700,
                borderColor: isDark ? "#334155" : "#cbd5e1",
                color: isDark ? "#cbd5e1" : "#475569",
              }}
            >
              Deny
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleAgree}
              sx={{
                borderRadius: 0,          // ← square button
                py: 1.2,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#16a34a",
                "&:hover": { bgcolor: "#15803d" },
              }}
            >
              Agree
            </Button>
          </Box>
        </Dialog>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}